// src/layouts/cartoes/index.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Card,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentIcon from "@mui/icons-material/Payment";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController } from "context";
import { firestore } from "firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, updateDoc, doc, query, orderBy, getDoc } from "firebase/firestore";
import { formatCurrency } from "examples/FormatCurrency";

/* ===== Imagens das bandeiras (ajuste se os nomes forem diferentes) ===== */
import visaPng from "assets/images/cards/visa.png";
import mastercardPng from "assets/images/cards/mastercard.png";
import eloPng from "assets/images/cards/elo.png";
import amexPng from "assets/images/cards/amex.png";
import hipercardPng from "assets/images/cards/hipercard.png";
import defaultCardPng from "assets/images/cards/default.png";

/* ===== Helpers ===== */

// converte strings como "R$ 1.234,56" -> 1234.56
const parseCurrencyValue = (valueString) => {
  if (!valueString) return 0;
  if (typeof valueString === "number") return valueString;
  let cleanedValue = valueString.toString().replace(/[^0-9,.]/g, "");
  if (cleanedValue.includes(",") && cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(/\./g, "");
    cleanedValue = cleanedValue.replace(",", ".");
  } else if (cleanedValue.includes(",") && !cleanedValue.includes(".")) {
    cleanedValue = cleanedValue.replace(",", ".");
  } else if (cleanedValue.includes(",")) {
    const parts = cleanedValue.split(",");
    const lastPart = parts.pop();
    const integerPart = parts.join("");
    cleanedValue = `${integerPart}.${lastPart}`;
  }
  return parseFloat(cleanedValue) || 0;
};

// unmask para máscara de moeda ao digitar (pega só dígitos e divide por 100)
const unmaskCurrency = (s) => Number(String(s).replace(/[^\d]/g, "")) / 100 || 0;

const parseBrDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return null;
  const [d, m, y] = ddmmyyyy.split("/").map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const getCicloAtual = (fechamentoDia, refDate = new Date()) => {
  const ref = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate(), 12);
  const fim =
    ref.getDate() > fechamentoDia
      ? new Date(ref.getFullYear(), ref.getMonth() + 1, fechamentoDia, 12)
      : new Date(ref.getFullYear(), ref.getMonth(), fechamentoDia, 12);
  const inicio = new Date(fim.getFullYear(), fim.getMonth() - 1, fechamentoDia + 1, 12);
  const fmt = (d) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return { inicio, fim, label: `${fmt(inicio)} – ${fmt(fim)}` };
};

const withinCycle = (date, inicio, fim) => {
  const t = date.getTime();
  return t >= inicio.getTime() && t <= fim.getTime();
};

// retorna um <img> com a logo
const logoPorBandeira = (b) => {
  const key = (b || "").toLowerCase();
  const map = {
    visa: visaPng,
    mastercard: mastercardPng,
    elo: eloPng,
    amex: amexPng,
    hipercard: hipercardPng,
  };
  const src = map[key] || defaultCardPng;
  return (
    <img src={src} alt={key || "cartão"} style={{ display: "block", width: 36, height: 24, objectFit: "contain" }} />
  );
};

const statusDaFatura = ({ total, pago, pendente }) => {
  if (total <= 0) return { label: "Sem lançamentos", color: "grey" };
  if (pendente === 0) return { label: "Paga", color: "green" };
  if (pago > 0 && pendente > 0) return { label: "Parcial", color: "orange" };
  return { label: "Pendente", color: "red" };
};

/* ===== Página ===== */

function Cartoes() {
  const [controller] = useMaterialUIController();
  const { darkMode, currency } = controller;

  const [uid, setUid] = useState(null);

  const [cartoes, setCartoes] = useState([]); // armazenados em usuarios/{uid}.cartoes (array)
  const [lancamentos, setLancamentos] = useState([]);

  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCartao, setDrawerCartao] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCartao, setEditingCartao] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payValor, setPayValor] = useState("");

  const [form, setForm] = useState({
    bandeira: "",
    fechamentoDia: "",
    vencimentoDia: "",
    limite: "",
    padrao: false,
    ativo: true,
  });

  /* Auth */
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user ? user.uid : null);
    });
    return () => unsub();
  }, []);

  /* Carregar cartões (de usuarios/{uid}.cartoes) + lançamentos */
  useEffect(() => {
    const fetchAll = async () => {
      if (!uid) return;
      setLoading(true);
      try {
        // cartões ficam no doc do usuário
        const userRef = doc(firestore, "usuarios", uid);
        const userSnap = await getDoc(userRef);
        const arr = userSnap.exists() ? userSnap.data().cartoes || [] : [];
        setCartoes(arr);

        // lançamentos continuam na subcoleção (regras já permitem)
        const qLanc = query(collection(firestore, "usuarios", uid, "lancamentos"), orderBy("data", "desc"));
        const lancSnap = await getDocs(qLanc);
        setLancamentos(lancSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Erro ao carregar cartões/lançamentos:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [uid]);

  const getLancamentosDoCartaoNoCiclo = (cartao) => {
    if (!cartao) return [];
    const { inicio, fim } = getCicloAtual(Number(cartao.fechamentoDia || 10));
    return lancamentos
      .filter((l) => l.formaPagamento === "Cartão de Crédito")
      .filter((l) => {
        const d = parseBrDate(l.data);
        if (!d || !withinCycle(d, inicio, fim)) return false;
        if (l.cartaoId) return l.cartaoId === cartao.id;
        if (!l.cartaoId && cartao.padrao) return true;
        return false;
      });
  };

  const cardResumo = (cartao) => {
    const lista = getLancamentosDoCartaoNoCiclo(cartao);
    const total = lista.reduce((acc, l) => acc + parseCurrencyValue(l.valor), 0);
    const pago = lista.filter((l) => l.pago).reduce((acc, l) => acc + parseCurrencyValue(l.valor), 0);
    const pendente = Math.max(total - pago, 0);
    return { total, pago, pendente, ciclo: getCicloAtual(Number(cartao.fechamentoDia || 10)) };
  };

  // total de lançamentos futuros (após o fim do ciclo atual)
  const totalFuturosDoCartao = (cartao) => {
    if (!cartao) return 0;
    const { fim } = getCicloAtual(Number(cartao.fechamentoDia || 10));

    return lancamentos
      .filter((l) => l.formaPagamento === "Cartão de Crédito")
      .filter((l) => {
        const d = parseBrDate(l.data);
        if (!d) return false;

        const pertence = (l.cartaoId && l.cartaoId === cartao.id) || (!l.cartaoId && cartao.padrao);
        if (!pertence) return false;

        return d.getTime() > fim.getTime();
      })
      .reduce((acc, l) => acc + parseCurrencyValue(l.valor), 0);
  };

  const abrirFatura = (cartao) => {
    setDrawerCartao(cartao);
    setDrawerOpen(true);
  };

  /* Abrir modal */
  const abrirDialogCartao = (cartao = null) => {
    if (cartao) {
      setEditingCartao(cartao);
      setForm({
        bandeira: cartao.bandeira || "",
        fechamentoDia: cartao.fechamentoDia ?? "",
        vencimentoDia: cartao.vencimentoDia ?? "",
        limite: formatCurrency(parseCurrencyValue(cartao.limite || 0), currency),
        padrao: !!cartao.padrao,
        ativo: cartao.ativo !== false,
      });
    } else {
      setEditingCartao(null);
      setForm({
        bandeira: "",
        fechamentoDia: "",
        vencimentoDia: "",
        limite: "",
        padrao: cartoes.length === 0, // primeiro vira padrão
        ativo: true,
      });
    }
    setDialogOpen(true);
  };

  /* Salvar no campo usuarios/{uid}.cartoes (array) */
  const salvarCartao = async () => {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    if (!userId) {
      alert("Faça login para salvar o cartão.");
      return;
    }

    const nowISO = new Date().toISOString();
    const payload = {
      id: editingCartao?.id || cryptoRandomId(),
      bandeira: form.bandeira || "visa",
      fechamentoDia: Number(form.fechamentoDia) || 1,
      vencimentoDia: Number(form.vencimentoDia) || 10,
      limite: parseCurrencyValue(form.limite) || 0,
      padrao: !!form.padrao,
      ativo: form.ativo !== false,
      createdAt: editingCartao?.createdAt || nowISO,
      updatedAt: nowISO,
    };

    try {
      const userRef = doc(firestore, "usuarios", userId);
      const snap = await getDoc(userRef);
      const atual = snap.exists() ? snap.data().cartoes || [] : [];

      // se marcar como padrão, desmarca os demais
      let lista = atual.map((c) => ({ ...c, padrao: payload.padrao ? false : c.padrao }));

      if (editingCartao) {
        lista = lista.map((c) => (c.id === editingCartao.id ? { ...payload } : c));
      } else {
        lista = [...lista, payload];
      }

      await updateDoc(userRef, { cartoes: lista });

      setCartoes(lista);
      setDialogOpen(false);
    } catch (err) {
      console.error("Erro ao salvar cartão:", err);
      alert("Não foi possível salvar. Verifique suas permissões e tente novamente.");
    }
  };

  /* Ativar/Desativar (também atualiza o array) */
  const desativarOuAtivar = async (cartao) => {
    if (!uid) return;
    try {
      const userRef = doc(firestore, "usuarios", uid);
      const snap = await getDoc(userRef);
      const lista = snap.exists() ? snap.data().cartoes || [] : [];
      const nova = lista.map((c) =>
        c.id === cartao.id ? { ...c, ativo: !cartao.ativo, updatedAt: new Date().toISOString() } : c
      );
      await updateDoc(userRef, { cartoes: nova });
      setCartoes(nova);
    } catch (e) {
      console.error("Erro ao ativar/desativar cartão:", e);
    }
  };

  /* Pagamento (lançamentos continuam na subcoleção) */
  const abrirPagamento = (cartao) => {
    const { pendente } = cardResumo(cartao);
    setPayValor(pendente > 0 ? formatCurrency(pendente, currency) : "");
    setDrawerCartao(cartao);
    setPayOpen(true);
  };

  const confirmarPagamento = async () => {
    if (!uid || !drawerCartao) return;
    try {
      const lista = getLancamentosDoCartaoNoCiclo(drawerCartao)
        .filter((l) => !l.pago)
        .sort((a, b) => parseBrDate(a.data) - parseBrDate(b.data));
      let saldoPagar = parseCurrencyValue(payValor) || lista.reduce((acc, l) => acc + parseCurrencyValue(l.valor), 0);

      for (const l of lista) {
        if (saldoPagar <= 0) break;
        const v = parseCurrencyValue(l.valor);
        await updateDoc(doc(firestore, "usuarios", uid, "lancamentos", l.id), { pago: true });
        saldoPagar -= v;
      }

      const qLanc = query(collection(firestore, "usuarios", uid, "lancamentos"), orderBy("data", "desc"));
      const lancSnap = await getDocs(qLanc);
      setLancamentos(lancSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      setPayOpen(false);
      setDrawerOpen(true);
    } catch (e) {
      console.error("Erro ao pagar fatura:", e);
    }
  };

  const listaFatura = useMemo(() => {
    if (!drawerCartao) return [];
    return getLancamentosDoCartaoNoCiclo(drawerCartao).sort((a, b) => parseBrDate(b.data) - parseBrDate(a.data));
  }, [drawerCartao, lancamentos]);

  /* ==== UI ==== */

  const renderBandeira = (value) => {
    if (!value) return <span style={{ color: "#9e9e9e" }}>Escolha o cartão</span>;
    const map = {
      visa: "Visa",
      mastercard: "Mastercard",
      elo: "Elo",
      amex: "American Express",
      hipercard: "Hipercard",
      outro: "Outro",
    };
    return map[value] || "Outro";
  };

  // máscara de moeda no campo "Limite"
  const handleLimiteChange = (e) => {
    const val = e.target.value;
    const num = unmaskCurrency(val);
    setForm((f) => ({ ...f, limite: formatCurrency(num, currency) }));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={1}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"}>
            Cartões de Crédito
          </MDTypography>

          <Button
            startIcon={<AddIcon />}
            onClick={() => abrirDialogCartao(null)}
            sx={{
              height: 36,
              borderRadius: "10px",
              px: 2,
              backgroundColor: darkMode ? "#444" : "#fff",
              color: darkMode ? "#fff" : "#1976d2",
              border: darkMode ? "1px solid #666" : "1px solid #ddd",
              textTransform: "none",
              fontSize: "0.9rem",
              "&:hover": { backgroundColor: darkMode ? "#555" : "#f0f0f0" },
            }}
          >
            Novo Cartão
          </Button>
        </MDBox>

        <Grid container spacing={2}>
          {loading && (
            <Grid item xs={12}>
              <Card
                style={{
                  padding: 16,
                  borderRadius: 12,
                  border: darkMode ? "1px solid #666" : "1px solid #eee",
                  background: darkMode ? "#333" : "#fff",
                }}
              >
                <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"}>
                  Carregando cartões...
                </MDTypography>
              </Card>
            </Grid>
          )}

          {!loading && cartoes.length === 0 && (
            <Grid item xs={12}>
              <Card
                style={{
                  padding: 16,
                  borderRadius: 12,
                  border: darkMode ? "1px solid #666" : "1px solid #eee",
                  background: darkMode ? "#333" : "#fff",
                }}
              >
                <MDTypography variant="body1" fontWeight="bold" mb={1}>
                  Nenhum cartão cadastrado
                </MDTypography>
                <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"} mb={2}>
                  Cadastre seu primeiro cartão para visualizar a fatura do ciclo e pagar com 1 clique.
                </MDTypography>
              </Card>
            </Grid>
          )}

          {!loading &&
            cartoes
              .filter((c) => c.ativo !== false)
              .map((cartao) => {
                const { total, pago, pendente, ciclo } = cardResumo(cartao);
                const badge = statusDaFatura({ total, pago, pendente });
                const futuros = totalFuturosDoCartao(cartao);

                return (
                  <Grid key={cartao.id} item xs={12} md={6} lg={4}>
                    <Card
                      style={{
                        padding: 16,
                        borderRadius: 12,
                        border: darkMode ? "1px solid #666" : "1px solid #eee",
                        background: darkMode ? "#2f2f2f" : "#fff",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDBox display="flex" alignItems="center" gap={1.25}>
                          <Box
                            sx={{
                              width: 36,
                              height: 24,
                              borderRadius: "6px",
                              background: darkMode ? "#3d3d3d" : "#f2f2f2",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            title={cartao.bandeira}
                          >
                            {logoPorBandeira(cartao.bandeira)}
                          </Box>
                          <MDTypography variant="h6" fontWeight="bold"></MDTypography>
                          {cartao.padrao && (
                            <span
                              style={{
                                fontSize: "0.7rem",
                                background: darkMode ? "#4a6a7d" : "#e0f7fa",
                                color: darkMode ? "#a3e4ff" : "#26c6da",
                                padding: "2px 8px",
                                borderRadius: 8,
                                marginLeft: 6,
                              }}
                            >
                              Padrão
                            </span>
                          )}
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <span
                            style={{
                              fontSize: "0.75rem",
                              background:
                                badge.color === "green"
                                  ? "#e8f5e9"
                                  : badge.color === "orange"
                                  ? "#fff3e0"
                                  : badge.color === "red"
                                  ? "#ffebee"
                                  : "#f5f5f5",
                              color:
                                badge.color === "green"
                                  ? "#2e7d32"
                                  : badge.color === "orange"
                                  ? "#ef6c00"
                                  : badge.color === "red"
                                  ? "#c62828"
                                  : "#666",
                              padding: "2px 8px",
                              borderRadius: 8,
                              fontWeight: 700,
                            }}
                          >
                            {badge.label}
                          </span>

                          {/* Pill de futuros */}
                          {futuros > 0 && (
                            <span
                              style={{
                                fontSize: "0.75rem",
                                background: darkMode ? "#2e3b55" : "#e3f2fd",
                                color: darkMode ? "#90caf9" : "#1565c0",
                                padding: "2px 8px",
                                borderRadius: 8,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                              }}
                              title="Lançamentos de parcelas ou compras com data futura"
                            >
                              Futuros: {formatCurrency(futuros, currency)}
                            </span>
                          )}

                          <Tooltip title="Editar / Ativar/Desativar">
                            <IconButton size="small" onClick={() => abrirDialogCartao(cartao)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      </MDBox>

                      <Divider style={{ margin: "12px 0", opacity: 0.6 }} />

                      <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"}>
                        Ciclo: {ciclo.label}
                      </MDTypography>

                      <MDBox mt={1.25} display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                        <MDBox
                          sx={{
                            p: 1,
                            borderRadius: "10px",
                            backgroundColor: darkMode ? "#3a3a3a" : "#fafafa",
                            border: darkMode ? "1px solid #555" : "1px solid #eee",
                          }}
                        >
                          <MDTypography variant="caption" color={darkMode ? "light" : "textSecondary"}>
                            Total do ciclo
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold">
                            {formatCurrency(total, currency)}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          sx={{
                            p: 1,
                            borderRadius: "10px",
                            backgroundColor: darkMode ? "#3a3a3a" : "#fafafa",
                            border: darkMode ? "1px solid #555" : "1px solid #eee",
                          }}
                        >
                          <MDTypography variant="caption" color={darkMode ? "light" : "textSecondary"}>
                            Pendente
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold" color={pendente > 0 ? "orange" : "green"}>
                            {formatCurrency(pendente, currency)}
                          </MDTypography>
                        </MDBox>
                      </MDBox>

                      <MDBox mt={1.5} display="flex" gap={1}>
                        <Button
                          onClick={() => abrirFatura(cartao)}
                          startIcon={<CreditCardIcon />}
                          sx={{
                            flex: 1,
                            height: 36,
                            borderRadius: "10px",
                            px: 2,
                            backgroundColor: darkMode ? "#444" : "#fff",
                            color: darkMode ? "#fff" : "#333",
                            border: darkMode ? "1px solid #666" : "1px solid #ddd",
                            textTransform: "none",
                            "&:hover": { backgroundColor: darkMode ? "#555" : "#f0f0f0" },
                          }}
                        >
                          Ver fatura
                        </Button>

                        {/* Só mostra o botão se houver algo pendente */}
                        {pendente > 0 && (
                          <Button
                            onClick={() => abrirPagamento(cartao)}
                            startIcon={<PaymentIcon />}
                            sx={{
                              flex: 1,
                              height: 36,
                              borderRadius: "10px",
                              px: 2,
                              backgroundColor: darkMode ? "#2e7d32" : "#e8f5e9",
                              color: darkMode ? "#c8e6c9" : "#2e7d32",
                              border: darkMode ? "1px solid #1b5e20" : "1px solid #c8e6c9",
                              textTransform: "none",
                              "&:hover": { backgroundColor: darkMode ? "#1b5e20" : "#dcedc8" },
                            }}
                          >
                            Pagar fatura
                          </Button>
                        )}
                      </MDBox>

                      <MDBox mt={1} display="flex" alignItems="center" justifyContent="space-between">
                        <MDTypography variant="caption" color={darkMode ? "light" : "textSecondary"}>
                          Vencimento dia {cartao.vencimentoDia}
                        </MDTypography>
                        <Button
                          size="small"
                          onClick={() => desativarOuAtivar(cartao)}
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            borderRadius: "8px",
                            px: 1.25,
                            backgroundColor: darkMode ? "#3d3d3d" : "#f5f5f5",
                            color: darkMode ? "#ddd" : "#555",
                            border: darkMode ? "1px solid #555" : "1px solid #e0e0e0",
                            "&:hover": { backgroundColor: darkMode ? "#444" : "#eee" },
                          }}
                        >
                          {cartao.ativo === false ? "Ativar" : "Desativar"}
                        </Button>
                      </MDBox>
                    </Card>
                  </Grid>
                );
              })}
        </Grid>
      </MDBox>

      {/* Drawer - Fatura */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420 },
            backgroundColor: darkMode ? "#2b2b2b" : "#fff",
          },
        }}
      >
        <MDBox p={2}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <MDTypography variant="h6" fontWeight="bold">
              {drawerCartao ? renderBandeira(drawerCartao.bandeira) : "Fatura"}
            </MDTypography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </MDBox>
          {drawerCartao && (
            <>
              <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"} mb={1}>
                Ciclo: {getCicloAtual(Number(drawerCartao.fechamentoDia || 10)).label}
              </MDTypography>
              <Divider />
              <MDBox mt={2} />
              {listaFatura.length === 0 ? (
                <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"}>
                  Nenhum lançamento de cartão neste ciclo.
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1.25}>
                  {listaFatura.map((l) => (
                    <Card
                      key={l.id}
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        border: darkMode ? "1px solid #555" : "1px solid #eee",
                        background: darkMode ? "#3a3a3a" : "#fafafa",
                      }}
                    >
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox display="flex" gap={1}>
                          <span style={{ fontSize: "1.2rem" }}>{l.emoji || "🏷️"}</span>
                          <MDBox>
                            <MDTypography variant="body2" fontWeight="bold">
                              {l.categoria}
                            </MDTypography>
                            <MDTypography variant="caption" color={darkMode ? "light" : "textSecondary"}>
                              {l.descricao || "—"} • {l.data}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDBox textAlign="right">
                          <MDTypography variant="body2" fontWeight="bold">
                            {formatCurrency(parseCurrencyValue(l.valor), currency)}
                          </MDTypography>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              background: l.pago
                                ? darkMode
                                  ? "#294436"
                                  : "#e8f5e9"
                                : darkMode
                                ? "#4a2b2b"
                                : "#ffebee",
                              color: l.pago ? (darkMode ? "#a5d6a7" : "#2e7d32") : darkMode ? "#ef9a9a" : "#c62828",
                              padding: "2px 8px",
                              borderRadius: 8,
                              fontWeight: 700,
                            }}
                          >
                            {l.pago ? "Pago" : "Pendente"}
                          </span>
                        </MDBox>
                      </MDBox>
                    </Card>
                  ))}
                </MDBox>
              )}
              <MDBox mt={2} display="flex" gap={1}>
                <Button
                  onClick={() => abrirPagamento(drawerCartao)}
                  startIcon={<PaymentIcon />}
                  sx={{
                    flex: 1,
                    height: 36,
                    borderRadius: "10px",
                    px: 2,
                    backgroundColor: darkMode ? "#2e7d32" : "#e8f5e9",
                    color: darkMode ? "#c8e6c9" : "#2e7d32",
                    border: darkMode ? "1px solid #1b5e20" : "1px solid #c8e6c9",
                    textTransform: "none",
                    "&:hover": { backgroundColor: darkMode ? "#1b5e20" : "#dcedc8" },
                  }}
                >
                  Pagar fatura
                </Button>
              </MDBox>
            </>
          )}
        </MDBox>
      </Drawer>

      {/* Dialog - Novo/Editar Cartão */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{editingCartao ? "Editar Cartão" : "Novo Cartão"}</span>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Select
                fullWidth
                value={form.bandeira}
                onChange={(e) => setForm((f) => ({ ...f, bandeira: e.target.value }))}
                displayEmpty
                sx={{ height: 42, borderRadius: "10px" }}
                renderValue={renderBandeira}
              >
                <MenuItem value="">
                  <em>Escolha o cartão</em>
                </MenuItem>
                <MenuItem value="visa">Visa</MenuItem>
                <MenuItem value="mastercard">Mastercard</MenuItem>
                <MenuItem value="elo">Elo</MenuItem>
                <MenuItem value="amex">American Express</MenuItem>
                <MenuItem value="hipercard">Hipercard</MenuItem>
                <MenuItem value="outro">Outro</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Fechamento (dia)"
                value={form.fechamentoDia}
                onChange={(e) => setForm((f) => ({ ...f, fechamentoDia: e.target.value }))}
                inputProps={{ min: 1, max: 31 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Vencimento (dia)"
                value={form.vencimentoDia}
                onChange={(e) => setForm((f) => ({ ...f, vencimentoDia: e.target.value }))}
                inputProps={{ min: 1, max: 31 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Limite (opcional)"
                placeholder={formatCurrency(0, currency)}
                value={form.limite}
                onChange={handleLimiteChange}
                inputProps={{ inputMode: "numeric" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CreditCardIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.padrao}
                    onChange={(e) => setForm((f) => ({ ...f, padrao: e.target.checked }))}
                  />
                }
                label="Definir como cartão padrão (captura lançamentos sem cartão)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch checked={form.ativo} onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} />
                }
                label="Cartão ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={salvarCartao}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog - Pagar Fatura */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: "12px",
          }),
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Pagar fatura</span>
          <IconButton onClick={() => setPayOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {drawerCartao && (
            <>
              <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"}>
                Cartão: <strong>{renderBandeira(drawerCartao.bandeira)}</strong>
              </MDTypography>
              <MDTypography variant="body2" color={darkMode ? "light" : "textSecondary"} mb={2}>
                Ciclo: {getCicloAtual(Number(drawerCartao.fechamentoDia || 10)).label}
              </MDTypography>
            </>
          )}

          <TextField
            fullWidth
            label="Para pagamento parcial, digite aqui"
            placeholder={formatCurrency(0, currency)}
            value={payValor}
            onChange={(e) => setPayValor(e.target.value)}
          />
          <MDTypography variant="caption" color={darkMode ? "light" : "textSecondary"}>
            Obs.: o valor parcial será aplicado em lançamentos pendentes do mais antigo para o mais recente.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPayOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={confirmarPagamento}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Confirmar pagamento
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

/* id único simples (sem libs) */
function cryptoRandomId() {
  try {
    const a = new Uint32Array(4);
    window.crypto.getRandomValues(a);
    return Array.from(a, (x) => x.toString(16)).join("");
  } catch {
    return `id_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
  }
}

export default Cartoes;
